import { Box, Skeleton } from '@mui/material';
import i18n from 'i18n';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import {
	createCustomer,
	fetchUserBalance,
	getActiveSubscription,
	getUserProfile,
} from 'store/user-service/asyncActions';
import { getPaymentMethods, getStripeInstance } from 'store/user-service/selector';
import { setCurrentSubscription } from 'store/user-service/userSlice';

import { flexMixin } from 'utils/flexMixin';

import { getEnvVarAsBoolean } from 'core/helpers/env-helper';
import { hub_events } from 'core/constants/hubEvents';
import { NamespacesList } from 'core/enums/common';
import { useGetPaymentMethods } from 'core/hooks/useGetPaymentMethods';
import { useGetUser } from 'core/hooks/useGetUser';
import { ActiveTariffPlan, PaymentMethodType } from 'core/types/base';

import { CustomActionTooltip } from 'components/shared/CustomActionTooltip';

import { ReactComponent as Card } from 'assets/icons/card.svg';

import { BillingHistoryModal } from './BillingHistoryModal';
import { CancelRenewalModal } from './CancelRenewalModal';
import { TariffPlansModal } from './TariffPlansModal';
import { UpdatePaymentInformationModal } from './UpdatePaymentInformation';
import { formatTariffEndDate } from './helpers';
import * as Styled from './styled';

export const Subscription = () => {
	const { t: profileTranslate } = useTranslation(NamespacesList['profilePage']);
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);

	const dispatch = useAppDispatch();

	const { user } = useGetUser();
	const { gettingPaymentLoading, handleGetAllPaymentsMethods } = useGetPaymentMethods();
	const [isOpenTariffPlans, setIsOpenTariffPlans] = useState(false);
	const [isOpenCancelRenewal, setIsOpenCancelRenewal] = useState(false);
	const [isOpenBillingHistory, setIsOpenBillingHistory] = useState(false);
	const [isOpenUpdatePaymentMethod, setIsOpenUpdatePaymentMethod] = useState(false);
	const [activeTariff, setActiveTariff] = useState<ActiveTariffPlan | null>(null);
	const [creatingCustomerLoading, setCreatingCustomerLoading] = useState(false);
	const [gettingActiveTariff, setGettingActiveTariff] = useState(false);

	const stripeInstance = useSelector(getStripeInstance);
	const paymentMethods = useSelector(getPaymentMethods);

	const userDefaultPaymentMethod: PaymentMethodType | null = useMemo(
		() => paymentMethods?.find((method: PaymentMethodType) => method.default === true) || null,
		[paymentMethods]
	);

	const handleActionTariffPlan = () => {
		setIsOpenTariffPlans((prev) => !prev);
	};

	const handleActionCancelRenewal = () => {
		setIsOpenCancelRenewal((prev) => !prev);
	};

	const handleActionBillingHistory = () => {
		setIsOpenBillingHistory((prev) => !prev);
	};

	const handleActionUpdatePaymentMethod = () => {
		setIsOpenUpdatePaymentMethod((prev) => !prev);
	};

	const handleGetActiveTariff = async () => {
		if (!getEnvVarAsBoolean('REACT_APP_ENABLE_PAYMENTS', true)) return;

		setGettingActiveTariff(true);
		const tariff = await dispatch(getActiveSubscription()).unwrap();
		setActiveTariff(tariff);
		setGettingActiveTariff(false);
	};

	const handleCreateCustomerAccount = async () => {
		setCreatingCustomerLoading(true);
		try {
			await dispatch(createCustomer()).unwrap();
			await dispatch(getUserProfile());
		} catch (error) {
			console.log(error);
		} finally {
			setCreatingCustomerLoading(false);
		}
	};

	useEffect(() => {
		if (user?.stripeCustomerId && user?.stripeCustomerId?.length > 0) {
			handleGetActiveTariff();
			if (!paymentMethods || paymentMethods?.length === 0) {
				handleGetAllPaymentsMethods();
			}
		}
	}, []);

	const userBillingCheck = useMemo(() => {
		return (
			user?.stripeCustomerId === null ||
			user?.stripeCustomerId === '' ||
			paymentMethods?.length === 0 ||
			!paymentMethods?.find((item: PaymentMethodType) => item.default === true)
		);
	}, [user?.stripeCustomerId, paymentMethods]);

	const getPlanTitle = useCallback(
		(planInfo: string) => {
			if (planInfo && planInfo.includes('/')) {
				const info = planInfo.split('/');
				if (i18n.language === 'en') {
					return info[0];
				}
				if (i18n.language === 'ar') {
					return info[1];
				}
			}
			return planInfo;
		},
		[i18n]
	);

	const handleGetTariffPlanName = useMemo(() => {
		const tariffName = getPlanTitle(activeTariff?.name || '');
		if (user?.accountPlan === null) {
			return profileTranslate('text.noPlanChoosed');
		}
		return `${profileTranslate('text.tariffPlan')} ${tariffName}`;
	}, [activeTariff, i18n.language, user?.accountPlan]);

	useEffect(() => {
		stripeInstance?.connection.on(hub_events.response.paymentSuccess, () => {
			dispatch(getUserProfile());
			handleGetActiveTariff();
			dispatch(fetchUserBalance());
		});

		stripeInstance?.connection.on(hub_events.response.subscriptionCanceled, () => {
			handleGetActiveTariff();
		});
	}, [stripeInstance]);

	useEffect(() => {
		if (activeTariff) dispatch(setCurrentSubscription(activeTariff));
	}, [activeTariff]);

	return (
		<>
			<Styled.InfoBox className="rtl-able">
				<Styled.InfoTitle>{profileTranslate('text.yourCurrentPlan')}</Styled.InfoTitle>
				<Styled.InfoContent>
					<Styled.Subscription>
						<Styled.SubscriptionInfo>
							{gettingActiveTariff ? (
								<Skeleton variant="rounded" width={160} height={25} animation="wave" />
							) : (
								<Styled.ActiveSubscriptionTitle>
									{handleGetTariffPlanName}
								</Styled.ActiveSubscriptionTitle>
							)}
							{gettingActiveTariff ? (
								<Skeleton variant="rounded" width={260} height={20} animation="wave" />
							) : (
								<>
							{!!user?.accountPlan && !activeTariff?.isCanceled && (
								<Styled.SubscriptionDescription>{`${profileTranslate(
									'text.nextPayment'
								)} 99$ ${profileTranslate('text.nextPaymentDate')} ${formatTariffEndDate(
									activeTariff?.endDate || ''
								)}`}</Styled.SubscriptionDescription>
							)}
									{activeTariff?.isCanceled && (
										<Styled.SubscriptionDescription>
											{profileTranslate('text.canceledPlan')}
										</Styled.SubscriptionDescription>
									)}
								</>
							)}
						</Styled.SubscriptionInfo>
						<Styled.SubscriptionButtons>
							<CustomActionTooltip
								tooltipMessage={commonTranslate('tooltips.addDefaultPaymentMethodTooltip')}
								check={userBillingCheck}
							>
								<Styled.SubscriptionButton
									onClick={handleActionTariffPlan}
									disabled={userBillingCheck || activeTariff?.isCanceled}
								>
									{profileTranslate('text.updateTariffPlan')}
								</Styled.SubscriptionButton>
							</CustomActionTooltip>
						{!!user?.accountPlan && !activeTariff?.isCanceled && (
							<Styled.SubscriptionButton onClick={handleActionCancelRenewal}>
								{profileTranslate('text.cancelRenewal')}
							</Styled.SubscriptionButton>
						)}
						</Styled.SubscriptionButtons>
					</Styled.Subscription>
				</Styled.InfoContent>
			</Styled.InfoBox>
			<Styled.InfoBox className="rtl-able">
				<Styled.InfoTitle>{profileTranslate('text.billingInformation')}</Styled.InfoTitle>
				<Styled.InfoContent>
					<Styled.Subscription>
						<Styled.SubscriptionInfo>
							{user?.stripeCustomerId !== null && user?.stripeCustomerId !== '' ? (
								<>
									<Styled.BillingTitle>
										{user?.firstName} {user?.lastName}
									</Styled.BillingTitle>
									<Styled.SubscriptionDescription>{user?.email}</Styled.SubscriptionDescription>
								</>
							) : (
								<Styled.SubscriptionDescription>
									{profileTranslate('text.toUnlockTheAbilityToPurchase')}
								</Styled.SubscriptionDescription>
							)}
						</Styled.SubscriptionInfo>

						<Styled.SubscriptionButtons>
							{user?.stripeCustomerId !== null && user?.stripeCustomerId !== '' ? (
								<CustomActionTooltip
									tooltipMessage={commonTranslate('tooltips.addDefaultPaymentMethodTooltip')}
									check={userBillingCheck}
								>
									<Styled.SubscriptionButton
										onClick={handleActionBillingHistory}
										disabled={userBillingCheck}
									>
										{profileTranslate('text.billingHistory')}
									</Styled.SubscriptionButton>
								</CustomActionTooltip>
							) : (
								<Styled.SubscriptionButton
									disabled={creatingCustomerLoading}
									onClick={handleCreateCustomerAccount}
								>
									{creatingCustomerLoading ? (
										<Box sx={{ ...flexMixin({ gap: 8 }) }}>
											<Oval
												height={24}
												width={24}
												color="rgba(0, 0, 0, 0.26)"
												wrapperStyle={{}}
												wrapperClass=""
												visible={true}
												ariaLabel="oval-loading"
												secondaryColor="rgba(0, 0, 0, 0.26)"
												strokeWidth={5}
												strokeWidthSecondary={5}
											/>
											{profileTranslate('text.createCustomer')}
										</Box>
									) : (
										profileTranslate('text.createCustomer')
									)}
								</Styled.SubscriptionButton>
							)}
						</Styled.SubscriptionButtons>
					</Styled.Subscription>
				</Styled.InfoContent>
			</Styled.InfoBox>
			<Styled.InfoBox className="rtl-able">
				<Styled.InfoTitle>{profileTranslate('text.paymentInformation')}</Styled.InfoTitle>
				<Styled.InfoContent>
					<Styled.Subscription>
						<Styled.PaymentInfo>
							<Card />
							{userDefaultPaymentMethod?.last4 ? (
								<Styled.SubscriptionDescription>
									{userDefaultPaymentMethod
										? `**** **** **** ${userDefaultPaymentMethod.last4}`
										: profileTranslate('text.noSelectedPayment')}
								</Styled.SubscriptionDescription>
							) : (
								<Styled.SimpleSubscriptionDescription>
									{userDefaultPaymentMethod
										? userDefaultPaymentMethod.type[0].toUpperCase() +
										  userDefaultPaymentMethod.type.slice(1)
										: profileTranslate('text.noSelectedPayment')}
								</Styled.SimpleSubscriptionDescription>
							)}
						</Styled.PaymentInfo>
						<Styled.SubscriptionButtons>
							<CustomActionTooltip
								tooltipMessage={commonTranslate('tooltips.createCustomerAccountTooltip')}
								check={user?.stripeCustomerId === null || user?.stripeCustomerId === ''}
							>
								<Styled.SubscriptionButton
									onClick={handleActionUpdatePaymentMethod}
									disabled={user?.stripeCustomerId === null || user?.stripeCustomerId === ''}
								>
									{profileTranslate('text.updatePaymentInformation')}
								</Styled.SubscriptionButton>
							</CustomActionTooltip>
						</Styled.SubscriptionButtons>
					</Styled.Subscription>
				</Styled.InfoContent>
			</Styled.InfoBox>
			<TariffPlansModal
				isOpen={isOpenTariffPlans}
				onClose={handleActionTariffPlan}
				isOnPurchase={false}
			/>
			<CancelRenewalModal isOpen={isOpenCancelRenewal} onClose={handleActionCancelRenewal} />
			<BillingHistoryModal isOpen={isOpenBillingHistory} onClose={handleActionBillingHistory} />
			<UpdatePaymentInformationModal
				previousUrlQuery="isProfile=true"
				isOpen={isOpenUpdatePaymentMethod}
				onClose={handleActionUpdatePaymentMethod}
				gettingPaymentsLoading={gettingPaymentLoading}
				paymentMethods={paymentMethods}
				getPaymentsMethods={handleGetAllPaymentsMethods}
			/>
		</>
	);
};
